import { Steps } from "../constants";

export interface ProductCondition {
  productTypeCode?: string;
  productTypeName?: string;
  productSubtypeCode?: string;
  productSubtypeName?: string;
}

export interface StepRuleConfig {
  stepId: string;
  defaultOptionCode?: string;
  defaultOptionName?: string;
  readOnly?: boolean;
  hidden?: boolean;
  excludedOptionCodes?: string[];
}

export interface UIRules {
  letStahlsChooseIndications?: {
    defaultValue: boolean;
    hidden: boolean;
  };
}

export interface ProductRule {
  condition: ProductCondition;
  stepRules: StepRuleConfig[];
  uiRules?: UIRules;
}

export const productRules: ProductRule[] = [
  // Embroidery > Embroidered Emblems: Border Type = Overlock (read-only)
  {
    condition: {
      productTypeCode: "EM",
      productSubtypeName: "Embroidered Emblems",
    },
    stepRules: [
      {
        stepId: Steps.BorderType,
        defaultOptionCode: "Overlock",
        readOnly: true,
      },
    ],
  },
  // FlexStyle: Hide color selection
  {
    condition: {
      productTypeCode: "FS",
    },
    stepRules: [
      {
        stepId: Steps.ThreadColors,
        hidden: true,
      },
    ],
  },
  // Flexbroidery: Hide color selection
  {
    condition: {
      productTypeName: "Flexbroidery",
    },
    stepRules: [
      {
        stepId: Steps.ThreadColors,
        hidden: true,
      },
    ],
  },
  // Sublimation/Perfect Print > Print Stitch Emblems
  {
    condition: {
      productTypeCode: "PP",
      productSubtypeCode: "PRI",
    },
    stepRules: [
      {
        stepId: Steps.BorderType,
        defaultOptionCode: "Overlock",
        readOnly: true,
      },
      {
        stepId: Steps.Backing,
        excludedOptionCodes: ["Semi Heat Seal"],
      },
      {
        stepId: Steps.EmblemPrintStitch,
        hidden: true,
      },
    ],
    uiRules: {
      letStahlsChooseIndications: {
        defaultValue: true,
        hidden: true,
      },
    },
  },
];

export function matchesCondition(
  condition: ProductCondition,
  productType: { code?: string | null; name?: string } | null,
  productSubtype: { code?: string | null; name?: string } | null
): boolean {
  if (condition.productTypeCode && productType?.code !== condition.productTypeCode) {
    return false;
  }
  if (condition.productTypeName && productType?.name !== condition.productTypeName) {
    return false;
  }
  if (condition.productSubtypeCode && productSubtype?.code !== condition.productSubtypeCode) {
    return false;
  }
  if (condition.productSubtypeName && productSubtype?.name !== condition.productSubtypeName) {
    return false;
  }

  return !!(
    condition.productTypeCode ||
    condition.productTypeName ||
    condition.productSubtypeCode ||
    condition.productSubtypeName
  );
}

export function getStepRuleConfig(
  stepId: string,
  productType: { code?: string | null; name?: string } | null,
  productSubtype: { code?: string | null; name?: string } | null
): StepRuleConfig | null {
  for (const rule of productRules) {
    if (matchesCondition(rule.condition, productType, productSubtype)) {
      const stepRule = rule.stepRules.find((sr) => sr.stepId === stepId);
      if (stepRule) return stepRule;
    }
  }
  return null;
}

export function findDefaultOption<T extends { code?: string | null; name?: string }>(
  options: T[] | undefined,
  stepRule: StepRuleConfig | null
): T | null {
  if (!options || !stepRule) return null;

  if (stepRule.defaultOptionCode) {
    const byCode = options.find(
      (opt) => opt.code?.toLowerCase() === stepRule.defaultOptionCode?.toLowerCase()
    );
    if (byCode) return byCode;
  }

  if (stepRule.defaultOptionName) {
    const byName = options.find(
      (opt) => opt.name?.toLowerCase() === stepRule.defaultOptionName?.toLowerCase()
    );
    if (byName) return byName;
  }

  return null;
}

export function isStepHiddenByRules(
  stepId: string,
  emblemData: Record<string, any>
): boolean {
  const productType = emblemData[Steps.ProductType];
  const productSubtype = emblemData[Steps.ProductSubtype];

  const stepRule = getStepRuleConfig(
    stepId,
    productType ? { code: productType.code, name: productType.name } : null,
    productSubtype ? { code: productSubtype.code, name: productSubtype.name } : null
  );

  return stepRule?.hidden ?? false;
}

export function getUIRules(
  productType: { code?: string | null; name?: string } | null,
  productSubtype: { code?: string | null; name?: string } | null
): UIRules | undefined {
  const rule = productRules.find((r) =>
    matchesCondition(r.condition, productType, productSubtype)
  );
  return rule?.uiRules;
}